import { action } from 'mobx'
import { useEffect, useRef } from 'react'
import { messageError } from 'reusable/mui/modals/message-error'
import { SelectOptionItem } from 'reusable/mui/select-with-label/SelectWithLabel'

export const htmlSpace = '\u00A0'
export const htmlDash = '\u2013'
export const htmlDashSeparator = htmlSpace + htmlDash + htmlSpace

export function cloneObject<T extends object>(obj: T) {
   return JSON.parse(JSON.stringify(obj)) as T
}

export const deferAction = (x: () => any, ms?: number) => setTimeout(action(x), ms)

export const prefersDarkMode = () => {
   if (typeof window === 'undefined') return false
   return window.matchMedia('(prefers-color-scheme: dark)')?.matches ?? false
}

/** Whether the operating system asks apps to keep motion to a minimum. */
export const prefersReducedMotion = () => {
   if (typeof window === 'undefined') return false
   return window.matchMedia('(prefers-reduced-motion: reduce)')?.matches ?? false
}

export function notUndefined<T>(
   x: T | undefined,
   message = 'Variable should not be undefined. Please check the stack to see where the error comes from.',
   showDialog = true): T {
   if (typeof x === 'undefined') {
      const error = Error(message)
      if (showDialog) {
         messageError(<>{message} <br /><br /><pre>{error.stack}</pre></>, "System Error (notUndefined)", {
            DialogProps: { maxWidth: 'xl', fullWidth: true }
         })
      }
      throw error
   }
   return x
}


export function getNameById(id: number | string | undefined, lookups: SelectOptionItem[]) {
   if (id === undefined) {
      return ""
   }
   return lookups.find(l => l.id === id)?.label
}


export function hasProperty(object: {} | undefined, v: PropertyKey) {
   return !!object ? Object.prototype.hasOwnProperty.call(v, 'length') : false
}

export function usePrevious<T = undefined>(value: T) {
   const ref = useRef<T>()
   useEffect(() => {
      ref.current = value
   }, [value])

   return ref.current
}

export function findScrollableAncestor(element: HTMLElement | null): HTMLElement | null {
   while (element && element !== document.body) {
      if (element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth) {
         const style = window.getComputedStyle(element);
         if (style.overflow === 'auto' || style.overflow === 'scroll') {
            return element;
         }
      }
      element = element.parentElement;
   }
   return null;
}
